import imageCompression from 'browser-image-compression';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import type { ProfilePhoto } from '@/types/models';
import { createPhotoId, normalizePhotos } from '@/utils';

const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1080,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 1.0,
} as const;

const MAX_PHOTOS = 3;

/**
 * Compress a cropped Blob, upload to `users/{uid}/photos/{photoId}-{filename}`,
 * and return a photo object with a stable ID and the Storage object path. The
 * stored filename extension is derived from the compressed output type so it
 * matches the actual encoded format.
 */
async function uploadProfilePhoto(
  uid: string,
  blob: Blob,
  filename: string
): Promise<ProfilePhoto> {
  const id = createPhotoId();
  const base = filename.split('.').slice(0, -1).join('.') || filename;
  const file = new File([blob], `${base}.jpg`, { type: blob.type || 'image/jpeg' });
  const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
  const ext = compressed.type === 'image/webp' ? 'webp' : 'jpg';
  const path = `users/${uid}/photos/${id}-${base}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, compressed);
  const url = await getDownloadURL(storageRef);
  return { id, url, path, createdAt: new Date().toISOString() };
}

/**
 * Compress + upload a new photo and return the updated photos array.
 * Throws if the current array is already at MAX_PHOTOS.
 */
export async function addProfilePhoto(
  uid: string,
  currentPhotos: unknown,
  blob: Blob,
  filename: string
): Promise<ProfilePhoto[]> {
  const list = normalizePhotos(currentPhotos);
  if (list.length >= MAX_PHOTOS) {
    throw new Error(`You can upload a maximum of ${MAX_PHOTOS} photos.`);
  }
  const photo = await uploadProfilePhoto(uid, blob, filename);
  return [...list, { ...photo, position: list.length }];
}

/**
 * Delete a photo from Firebase Storage by its object path (falling back to its
 * download URL). Already-deleted and non-Storage references are tolerated so
 * callers can fire-and-forget cleanup.
 */
export async function deleteProfilePhoto(photo: ProfilePhoto | string): Promise<void> {
  const target = typeof photo === 'string' ? photo : photo.path || photo.url;
  try {
    await deleteObject(ref(storage, target));
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;
    if (code === 'storage/object-not-found') {
      console.warn('Photo already deleted from storage:', err);
    } else if (code === 'storage/invalid-url') {
      console.warn('Photo URL is not a Firebase Storage reference, removing from profile:', err);
    } else {
      throw err;
    }
  }
}

/**
 * Delete a photo from Firebase Storage and return the updated photos array
 * with that entry removed.
 */
export async function removeProfilePhoto(
  currentPhotos: unknown,
  index: number
): Promise<ProfilePhoto[]> {
  const list = normalizePhotos(currentPhotos);
  const photo = list[index];
  if (!photo) return list;
  await deleteProfilePhoto(photo);
  return list.filter((_, idx) => idx !== index).map((entry, position) => ({ ...entry, position }));
}
